<template>
  <PageShell title="Review" subtitle="Confirm your lot details before adding to inventory">
    <template #actions>
      <button class="ll-pill ghost" type="button" @click="$emit('back')" :disabled="saving">
        Back
      </button>
      <button class="ll-pill" type="button" @click="confirm" :disabled="saving">
        {{ saving ? "Saving..." : "Add Inventory" }}
      </button>
    </template>

    <div class="wrap">
      <!-- Summary -->
      <section class="card">
        <div class="card-title">Acquisition Summary</div>

        <div class="summary">
          <label class="field">
            <div class="label">Source</div>
            <input class="input" v-model="localDraft.acquisition_summary.source" placeholder="e.g., eBay, estate sale" />
          </label>

          <label class="field">
            <div class="label">Date Acquired</div>

            <input
              v-if="supportsDate"
              ref="dateInput"
              class="input"
              type="date"
              :value="dateISO"
              @input="onDateISO($event.target.value)"
            />

            <input
              v-else
              class="input"
              type="text"
              inputmode="numeric"
              placeholder="MM/DD/YYYY"
              :value="dateMMDD"
              @input="onDateMMDD($event.target.value)"
            />

            <div class="micro">MM/DD/YYYY</div>
          </label>

          <label class="field">
            <div class="label">Total Cost</div>
            <input class="input" type="number" step="0.01" :value="computedTotal" disabled />
            <div class="micro">Computed from item qty × unit cost</div>
          </label>

          <div class="field">
            <div class="label">Entry</div>
            <div class="pill">{{ localDraft.acquisition_summary.entry_method }}</div>
          </div>
        </div>
      </section>

      <!-- Flags / warnings -->
      <section v-if="(localDraft.flags || []).length" class="card warn">
        <div class="card-title">Review Notes</div>
        <ul class="flags">
          <li v-for="(f, i) in localDraft.flags" :key="i" class="flag">
            <span class="flag-dot" :class="f.severity"></span>
            <span class="flag-text">{{ f.message }}</span>
          </li>
        </ul>
      </section>

      <!-- Items -->
      <section class="card">
        <div class="card-title">Items</div>

        <div class="table-container">
          <table class="grid">
            <thead>
              <tr>
                <th style="width: 30%;">Item</th>
                <th style="width: 9%;">Qty</th>
                <th style="width: 12%;">Unit Cost</th>
                <th style="width: 14%;">Category</th>
                <th style="width: 9%;">Year</th>
                <th style="width: 26%;">Notes</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(it, idx) in localDraft.items" :key="idx">
                <td data-label="Item">
                  <input class="cell" v-model="it.name" placeholder="Item name" />
                </td>

                <td data-label="Qty">
                  <input class="cell" type="number" min="1" v-model.number="it.quantity" />
                </td>

                <td data-label="Unit Cost">
                  <!-- Supports either it.unit_cost or it.cost (manual/assistant draft) -->
                  <input
                    class="cell"
                    type="number"
                    step="0.01"
                    :value="getUnitCost(it)"
                    @input="setUnitCost(it, $event.target.value)"
                  />
                  <div v-if="getUnitCost(it) != null && Number(getUnitCost(it)) < 0" class="err">
                    Cost cannot be negative.
                  </div>
                </td>

                <td data-label="Category">
                  <input class="cell" v-model="it.category" placeholder="Category" />
                </td>

                <td data-label="Year">
                  <input class="cell" type="number" v-model.number="it.year" placeholder="Year" />
                </td>

                <td data-label="Notes">
                  <input class="cell" v-model="it.notes" placeholder="Optional notes" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Bottom actions (desktop + fallback) -->
        <div class="footer-actions desktop-actions">
          <button class="ll-pill ghost" type="button" @click="$emit('cancel')" :disabled="saving">
            Cancel
          </button>
          <button class="ll-pill" type="button" @click="confirm" :disabled="saving">
            {{ saving ? "Saving..." : "Add Inventory" }}
          </button>
        </div>
      </section>

      <!-- Sticky mobile actions -->
      <div class="sticky-actions mobile-only">
        <button class="ll-pill ghost" type="button" @click="$emit('back')" :disabled="saving">
          Back
        </button>
        <button class="ll-pill" type="button" @click="confirm" :disabled="saving">
          {{ saving ? "Saving..." : "Add Inventory" }}
        </button>
      </div>
    </div>
  </PageShell>
</template>

<script>
import api from "@/services/api";
import PageShell from "@/components/layout/PageShell.vue";

export default {
  name: "DraftReview",
  components: { PageShell },
  props: {
    draft: { type: Object, required: true },
  },

  data() {
    return {
      localDraft: JSON.parse(JSON.stringify(this.draft)),
      saving: false,
      supportsDate: true,
    };
  },

  computed: {
    computedTotal() {
      const items = this.localDraft.items || [];
      const sum = items
        .filter((it) => this.getUnitCost(it) !== null && this.getUnitCost(it) !== undefined && this.getUnitCost(it) !== "")
        .reduce((acc, it) => acc + (Number(this.getUnitCost(it) || 0) * Number(it.quantity || 1)), 0);

      return Number(sum.toFixed(2));
    },

    dateMMDD() {
      const v = this.localDraft?.acquisition_summary?.date || "";
      if (!v) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return this.isoToMMDDYYYY(v);
      return v;
    },

    dateISO() {
      const v = this.localDraft?.acquisition_summary?.date || "";
      if (!v) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
      return this.mmddyyyyToISO(v) || "";
    },
  },

  watch: {
    draft: {
      handler(n) {
        this.localDraft = JSON.parse(JSON.stringify(n));
      },
      deep: true,
    },
    computedTotal(newTotal) {
      if (!this.localDraft.acquisition_summary) this.localDraft.acquisition_summary = {};
      this.localDraft.acquisition_summary.total_cost = newTotal;
    },
  },

  mounted() {
    this.supportsDate = this.detectDateSupport();
  },

  methods: {
    // ✅ Cost compatibility: supports { cost } drafts and { unit_cost } drafts
    getUnitCost(it) {
      if (it.unit_cost !== undefined && it.unit_cost !== null && it.unit_cost !== "") return it.unit_cost;
      if (it.cost !== undefined && it.cost !== null && it.cost !== "") return it.cost;
      return null;
    },
    setUnitCost(it, v) {
      const parsed = v === "" ? null : Number(v);
      it.unit_cost = Number.isNaN(parsed) ? null : parsed;
      // Keep cost in sync if it exists in this draft shape
      if ("cost" in it) it.cost = it.unit_cost;
    },

    validateBeforeConfirm() {
      const problems = [];

      if (!this.localDraft.acquisition_summary?.source?.trim()) {
        problems.push("Source is required.");
      }
      if (!this.localDraft.acquisition_summary?.date?.trim()) {
        problems.push("Date acquired is required.");
      }

      (this.localDraft.items || []).forEach((it, idx) => {
        if (!it.name || !it.name.trim()) problems.push(`Row ${idx + 1}: item name is required.`);
        if (!it.category || !String(it.category).trim()) problems.push(`Row ${idx + 1}: category is required.`);
        if (it.quantity == null || Number(it.quantity) < 1) problems.push(`Row ${idx + 1}: qty must be 1 or more.`);

        const u = this.getUnitCost(it);
        if (u == null || u === "" || Number.isNaN(Number(u))) problems.push(`Row ${idx + 1}: unit cost is required.`);
        if (u != null && Number(u) < 0) problems.push(`Row ${idx + 1}: unit cost cannot be negative.`);
      });

      if (problems.length) {
        alert(problems.join("\n"));
        return false;
      }
      return true;
    },

    detectDateSupport() {
      const i = document.createElement("input");
      i.setAttribute("type", "date");
      i.value = "2026-01-21";
      return i.type === "date" && i.value === "2026-01-21";
    },

    onDateISO(iso) {
      if (!this.localDraft.acquisition_summary) this.localDraft.acquisition_summary = {};
      this.localDraft.acquisition_summary.date = this.isoToMMDDYYYY(iso);
    },

    onDateMMDD(mmdd) {
      if (!this.localDraft.acquisition_summary) this.localDraft.acquisition_summary = {};
      this.localDraft.acquisition_summary.date = mmdd;
    },

    isoToMMDDYYYY(iso) {
      if (!iso || typeof iso !== "string") return "";
      const [y, m, d] = iso.split("-");
      if (!y || !m || !d) return "";
      return `${m}/${d}/${y}`;
    },

    mmddyyyyToISO(mmdd) {
      if (!mmdd || typeof mmdd !== "string") return "";
      const parts = mmdd.split("/");
      if (parts.length !== 3) return "";
      const [m, d, y] = parts;
      if (!m || !d || !y) return "";
      const mm = String(m).padStart(2, "0");
      const dd = String(d).padStart(2, "0");
      const yyyy = String(y);
      if (yyyy.length !== 4) return "";
      return `${yyyy}-${mm}-${dd}`;
    },

    async confirm() {
      if (!this.validateBeforeConfirm()) return;

      try {
        this.saving = true;

        // Normalize/validate server-side
        const { data: normalized } = await api.post("/acquisitions/draft", this.localDraft);

        // Persist
        const { data: result } = await api.post("/acquisitions/confirm", normalized);

        this.$emit("confirm", result);
      } catch (err) {
        const msg =
          err?.response?.data?.errors?.join("\n") ||
          err?.response?.data?.error ||
          err.message ||
          "Failed to confirm acquisition.";
        alert(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.wrap {
  display: grid;
  gap: 12px;
}

/* Card look matches Dashboard rows */
.card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
}

.card-title {
  font-weight: 900;
  font-size: 14px;
  letter-spacing: 0.2px;
  margin-bottom: 10px;
}

.card.warn {
  border-color: rgba(159, 50, 7, 0.35);
  background: rgba(255, 246, 241, 0.85);
}

/* Summary grid */
.summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 10px;
}

.field {
  display: grid;
  gap: 6px;
}

.label {
  font-weight: 900;
  font-size: 12px;
  opacity: 0.78;
}

.input {
  width: 100%;
  min-width: 0;
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 700;
  font-size: 16px;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: rgba(50, 123, 168, 0.55);
}

.micro {
  font-size: 12px;
  opacity: 0.7;
  font-weight: 700;
}

.pill {
  background: #e6eef3;
  border: 1px solid #b0c4de;
  border-radius: 999px;
  padding: 10px 12px;
  display: inline-flex;
  align-items: center;
  font-weight: 900;
  text-transform: lowercase;
  width: fit-content;
}

/* Flags */
.flags {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
}

.flag {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.flag-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 6px;
  background: #327ba8;
}

.flag-dot.warning {
  background: #9f3207;
}
.flag-dot.info {
  background: #327ba8;
}
.flag-dot.error {
  background: #b00020;
}

.flag-text {
  font-weight: 750;
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.25rem;
}

/* Table */
.table-container {
  background: rgba(255, 255, 255, 0.85);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
}

.grid {
  width: 100%;
  border-collapse: collapse;
}

.grid th,
.grid td {
  border-bottom: 1px solid #e6eef3;
  padding: 10px;
  text-align: left;
  vertical-align: top;
}

.grid th {
  background: #e6eef3;
  font-weight: 900;
}

.cell {
  width: 100%;
  min-width: 0;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 700;
  font-size: 16px;
  box-sizing: border-box;
}

.err {
  margin-top: 6px;
  color: #9f3207;
  font-weight: 900;
  font-size: 0.85rem;
}

.footer-actions {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.desktop-actions {
  display: flex;
}

.mobile-only {
  display: none;
}

.sticky-actions {
  position: sticky;
  bottom: 0;
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.10);
  padding: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Responsive */
@media (max-width: 900px) {
  .summary {
    grid-template-columns: 1fr;
  }

  /* Turn table into cards on mobile */
  .table-container {
    border-radius: 16px;
    overflow: visible;
    border: none;
    background: transparent;
  }

  .grid {
    min-width: 0;
    border-collapse: separate;
    border-spacing: 0 10px;
  }

  .grid thead {
    display: none;
  }

  .grid tr {
    display: block;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(131, 167, 203, 0.85);
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  }

  .grid td {
    display: block;
    padding: 8px 0;
    border: none;
  }

  .grid td::before {
    content: attr(data-label);
    display: block;
    font-size: 12px;
    font-weight: 900;
    opacity: 0.7;
    margin-bottom: 6px;
  }

  .desktop-actions {
    display: none;
  }

  .mobile-only {
    display: block;
  }

  :deep(.ll-pill) {
    width: 100%;
  }
}
</style>
